<template>
  <div class="i-coach-school-display">
    <div class="header">{{ COPY_DATA[testTakingPurposeKey].h3[0] }}</div>
    <div class="sub-header">{{ COPY_DATA[testTakingPurposeKey].h3[1] }}</div>
    <div class="schools">
      <template
        v-for="(school, index) in arrayOfSchools"
        :key="index"
      >
        <img
          v-show="(!isLOrLarger && index != 7) || isLOrLarger"
          :src="school"
        />
      </template>
    </div>
    <div class="coaches">
      <Carousel
        :autoplay="5000"
        :items-to-scroll="1"
        :items-to-show="iemsToShow"
        class="coaches-carousel"
        wrap-around
        snap-align="start"
      >
        <Slide
          v-for="(coach, index) in COACHES_DATA"
          :key="index"
        >
          <div class="coach">
            <img
              class="image"
              :src="coach.image"
            />
            <div class="bottom">
              <div class="name">{{ coach.name }}</div>
              <div class="rating"><img :src="ImageStars" /> {{ coach.rating }} stars</div>
              <div class="highlights">
                <strong>HIGHLIGHTS</strong>
                <ul>
                  <li>
                    <strong>{{ coach.clients }}</strong> clients served
                  </li>
                  <li>
                    <strong>{{ coach.years }}</strong> years of experience
                  </li>
                  <li>
                    Specializes in <strong>{{ coach.specializesIn }}</strong>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </Slide>
        <template #addons>
          <navigation>
            <template #next>
              <span>
                <img
                  :src="iconArrowRightGray"
                  alt="next-icon"
                />
              </span>
            </template>
            <template #prev>
              <span>
                <img
                  :src="iconArrowLeftGray"
                  alt="prev-icon"
                />
              </span>
            </template>
          </navigation>
          <Pagination />
        </template>
      </Carousel>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue"
import { useReportStore } from "@/stores/report"
import { Carousel, Navigation, Pagination, Slide } from "vue3-carousel"
import { breakpointLg, breakpointMd, breakpointSm, breakpointXl } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"

import { COACHES_DATA } from "@/helpers/coaches"
import { COPY_DATA } from "@/helpers/startPageDynamicData"

// ASSETS
import ImageStars from "@/assets/images/five-orange-stars.png"
import iconArrowLeftGray from "@/assets/icons/icon-arrow-left-gray.svg"
import iconArrowRightGray from "@/assets/icons/icon-arrow-right-gray.svg"
import imageSchool12 from "@/assets/welcome/school-princeton.svg"
import imageSchool11 from "@/assets/welcome/school-cornell.svg"
import imageSchool10 from "@/assets/welcome/school-yale.svg"
import imageSchool9 from "@/assets/welcome/school-penn.svg"
import imageSchool8 from "@/assets/welcome/school-mit.svg"
import imageSchool7 from "@/assets/welcome/school-duke.svg"
import imageSchool6 from "@/assets/welcome/school-harvard.svg"
import imageSchool5 from "@/assets/welcome/school-north.svg"
import imageSchool4 from "@/assets/welcome/school-chicago.svg"
import imageSchool3 from "@/assets/welcome/school-stanford.svg"
import imageSchool2 from "@/assets/welcome/school-hopkins.svg"
import imageSchool1 from "@/assets/welcome/school-ucla.svg"

// STORES
const reportStore = useReportStore()

//CONSTANTS
const arrayOfSchools = [
  imageSchool1,
  imageSchool2,
  imageSchool3,
  imageSchool4,
  imageSchool5,
  imageSchool6,
  imageSchool7,
  "",
  imageSchool8,
  imageSchool9,
  imageSchool10,
  imageSchool11,
  imageSchool12
]

// COMPOSABLES
const isSmOrLarger = useIsScreenSize(breakpointSm)
const isMdOrLarger = useIsScreenSize(breakpointMd)
const isLOrLarger = useIsScreenSize(breakpointLg)
const isXLorLarger = useIsScreenSize(breakpointXl)

//COMPUTED
const iemsToShow = computed(() => {
  return isXLorLarger.value ? 4.5 : isLOrLarger.value ? 3.5 : isMdOrLarger.value ? 2.5 : isSmOrLarger.value ? 2 : 1.5
})
const testTakingPurposeKey = computed(() => {
  return (reportStore.getTestTakingPurpose?.id || "other") as keyof typeof COPY_DATA
})
</script>

<style scoped lang="scss">
.i-coach-school-display {
  padding: 20px 16px 0;
  margin-bottom: 50px;

  .header {
    @include display-sm;
    @include flex(column, start, center);

    font-weight: 700;
    text-align: center;
    max-width: 706px;
    margin: auto;

    @media screen and (min-width: $breakpoint-sm) {
      font-size: 40px;
      line-height: 50px;
    }
  }

  .sub-header {
    @include text-md;

    margin: 15px auto 40px;
    font-weight: 500;
    text-align: center;
    max-width: 696px;
  }

  .coaches-carousel {
    width: 100%;

    .coach {
      @include flex($direction: column, $justify: start, $align: start, $gap: 5px);

      border-radius: 16px;
      box-shadow: 0 10px 25px 1px rgb(0 0 0 / 5%);
      width: 100%;
      height: 100%;
      margin: 0 8px;

      .image {
        width: 100%;
        border-radius: 16px;
        object-position: center;
      }

      .bottom {
        padding: 15px;
        text-align: left;

        .name {
          @include text-lg;

          font-weight: 700;

          @media screen and (min-width: $breakpoint-sm) {
            @include text-xl;
          }
        }

        .rating {
          @include text-xs;

          font-weight: 500;

          @media screen and (min-width: $breakpoint-sm) {
            @include text-sm;
          }

          > img {
            vertical-align: sub;
            width: 90px;

            @media screen and (min-width: $breakpoint-md) {
              width: unset;
            }
          }
        }

        .highlights {
          @include flex(column, start, start, $gap: 3px);
          @include text-xs;

          margin-top: 10px;

          @media screen and (min-width: $breakpoint-sm) {
            @include text-sm;
          }

          ul {
            padding-left: 15px;

            li {
              list-style-type: disc;
            }
          }

          strong {
            @include text-xs;
          }
        }
      }
    }
  }

  .schools {
    @include grid($cols: repeat(3, 1fr), $rows: auto, $align-items: center);

    justify-items: center;
    margin-bottom: 40px;

    @media screen and (min-width: $breakpoint-md) {
      grid-template-columns: repeat(4, 1fr);
    }

    @media screen and (min-width: $breakpoint-lg) {
      grid-template-columns: repeat(7, 1fr);
    }
  }
}
</style>

<style lang="scss">
.i-coach-school-display {
  /* stylelint-disable */
  .carousel__next,
  .carousel__prev {
    margin: 0 12%;
    top: calc(100% - 8px);

    @media screen and (min-width: $breakpoint-sm) {
      margin: 0 25%;
    }

    @media screen and (min-width: $breakpoint-md) {
      margin: 0 35%;
    }

    @media screen and (min-width: $breakpoint-lg) {
      margin: 0 38%;
    }

    @media screen and (min-width: $breakpoint-xl) {
      margin: 0 40%;
    }
  }

  .carousel__pagination-button {
    --vc-pgn-width: 5px;
    --vc-pgn-height: 5px;
    --vc-pgn-border-radius: 100%;
    @include border-radius-rounded;
  }

  .carousel__slide {
    margin-bottom: 25px;
  }
  /* stylelint-enable */
}
</style>
